import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom'
import * as Yup from "yup";
import { otpVerify, resendOtp, signupUser } from '../../slices/authSlice';
import { showPromiseNotification } from '../../common/notification';
import moment from "moment";
import { decryption } from '../../common/enc_dec';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import '../../i18';

export const OtpVerification = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state.auth.userInfo);
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const encryptedData = queryParams.get('data');

  const [isLoading, setIsLoading] = useState(false)
  const [isExpired, setIsExpired] = useState(false);
  const [remainingTimer, setRemainingTimer] = useState(0);

  const [timer, setTimer] = useState(() => {
    const storedTimer = localStorage.getItem("otpTimer");
    return storedTimer ? parseInt(atob(storedTimer), 10) : 60;
  });
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  const resendCountdownRef = useRef(null);
  const otpExpiryCountdownRef = useRef(null);

  const otpSchema = Yup.object().shape({
    otp: Yup.array()
      .of(
        Yup.string()
          .matches(/^\d$/, t("digitValidation"))
          .required(t("required", { field: "OTP" }))
      )
      .min(4, t("otpValidation"))
      .max(4, t("otpValidation")),
  });

  const formOptions = {
    resolver: yupResolver(otpSchema),
    defaultValues: {
      otp: ["", "", "", ""], // Adjust based on the length of OTP
    },
  };
  const { register, handleSubmit, formState, setValue, getValues, reset } = useForm(formOptions);
  const { errors } = formState;

  useEffect(() => {
    if (encryptedData) {
      try {
        const decryptedData = decryption(decodeURIComponent(encryptedData));
        setUserData(decryptedData);
      } catch (error) {
        console.error('Failed to decrypt user data:', error);
      }
    }
  }, [encryptedData]);


  useEffect(() => {
    const storedTimer = localStorage.getItem("otpTimer");
    const parsedTimer = storedTimer ? parseInt(atob(storedTimer), 10) : 60;
    setTimer(parsedTimer);

    if (parsedTimer > 0) {
      resendCountdownRef.current = setInterval(() => {
        setTimer((prevTimer) => {
          const newTimer = prevTimer - 1;
          localStorage.setItem("otpTimer", btoa(newTimer.toString())); // Encode new timer value with btoa
          if (newTimer <= 0) {
            clearInterval(resendCountdownRef.current);
            setCanResend(true); // Enable resend button when timer reaches 0
          }
          return newTimer;
        });
      }, 1000);
    } else {
      setCanResend(true); // Enable resend button if timer is already at 0
    }

    // Clean up interval on component unmount or re-render
    return () => clearInterval(resendCountdownRef.current);
  }, []);

  useEffect(() => {
    const storedGenerateTime = localStorage.getItem("otpGenerateTime");
    const generateTime = storedGenerateTime ? atob(storedGenerateTime) : null;

    if (generateTime) {

      const generateTimestamp = moment.utc(generateTime, 'YYYY-MM-DD HH:mm:ss').valueOf(); // Convert generateTime to timestamp
      const expirationTime = generateTimestamp + 1 * 60 * 1000; // 3 minutes from generateTime
      const currentTime = new Date().getTime();

      if (currentTime >= expirationTime) {
        setRemainingTimer(0);
        setCanResend(true);
        setIsExpired(true);
        return;
      }

      // Calculate initial remaining time
      const remainingTime = Math.floor((expirationTime - currentTime) / 1000);
      setRemainingTimer(remainingTime);

      // Start countdown
      otpExpiryCountdownRef.current = setInterval(() => {
        const newCurrentTime = new Date().getTime();
        const timeLeft = Math.floor((expirationTime - newCurrentTime) / 1000);

        if (timeLeft <= 0) {
          clearInterval(otpExpiryCountdownRef.current);
          setRemainingTimer(0);
          setIsExpired(true);
          setCanResend(true); // Enable resend when time runs out
        } else {
          setRemainingTimer(timeLeft);
        }
      }, 1000);

    }

    return () => clearInterval(otpExpiryCountdownRef.current);
  }, [userData]);

  const handleResendOtp = useCallback(async () => {
    setIsExpired(false);
    reset({
      otp: ["", "", "", ""] // Reset to empty values, clears input fields
    });
    inputRefs.current.forEach((input) => {
      if (input) {
        input.value = ""; // Clear the input field
      }
    });

    // Clear previous intervals before starting new ones
    if (resendCountdownRef.current) {
      clearInterval(resendCountdownRef.current);
    }
    if (otpExpiryCountdownRef.current) {
      clearInterval(otpExpiryCountdownRef.current);
    }
    const myPromise = new Promise((resolve, reject) => {
      setIsLoading(true);
      let payload = {
        is_forget: userData?.is_forget,
      }
      if (userData?.email) {
        payload.email = userData?.email
      }
      else {
        payload.country_code = userData?.country_code;
        payload.phone_number = userData?.phone_number;
      }
      dispatch(
        resendOtp({
          payload
        })
      )
        .then((response) => {
          if (response.payload.code === 200) {
            resolve(response.payload);

            setTimer(60); // Reset timer to 59 seconds
            localStorage.setItem("otpTimer", btoa("60")); // Update localStorage with new timer value encoded with btoa
            setCanResend(false); // Disable resend button

            // Reset generate_time for the OTP (you may get a new time in the response)
            const newGenerateTime = response.payload.data.generate_time;
            localStorage.setItem("otpGenerateTime", btoa(newGenerateTime));

            const newGenerateTimestamp = moment.utc(newGenerateTime, 'YYYY-MM-DD HH:mm:ss').valueOf();
            const newExpirationTime = newGenerateTimestamp + 1 * 60 * 1000; // 3 minutes from the new generate_time

            // Reset remaining timer for OTP expiration
            const newRemainingTime = Math.floor((newExpirationTime - new Date().getTime()) / 1000);
            setRemainingTimer(newRemainingTime);


            // Start countdown for OTP expiration
            otpExpiryCountdownRef.current = setInterval(() => {
              setRemainingTimer((prevRemainingTime) => {
                const newTimeLeft = prevRemainingTime - 1;
                if (newTimeLeft <= 0) {
                  clearInterval(otpExpiryCountdownRef.current);
                  setRemainingTimer(0);
                  setIsExpired(true); // Mark as expired
                  setCanResend(true); // Enable resend button
                }
                return newTimeLeft;
              });
            }, 1000);

            // Start countdown
            resendCountdownRef.current = setInterval(() => {
              setTimer((prevTimer) => {
                const newTimer = prevTimer - 1;
                localStorage.setItem("otpTimer", btoa(newTimer.toString())); // Encode new timer value with btoa
                if (newTimer <= 0) {
                  clearInterval(resendCountdownRef.current);
                  setCanResend(true); // Enable resend button when timer reaches 0
                }
                return newTimer;
              });
            }, 1000);

            // Clean up interval on component unmount or re-render
            return () => {
              clearInterval(resendCountdownRef.current);
              clearImmediate(otpExpiryCountdownRef.current);
            }
          } else {
            reject(response.payload);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    });

    if (!isLoading) {
      showPromiseNotification(myPromise);
    }


  }, [dispatch, userData?.is_forget, userData?.country_code, userData?.phone_number, isLoading, reset, userData?.email]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    const currentOtp = getValues("otp");
    const newOtp = [...currentOtp];
    newOtp[index] = value;

    if (value.length === 1 && index < inputRefs.current.length - 1) {
      // Move to the next input field
      inputRefs.current[index + 1].focus();
    }

    // Handle backspace
    if (value === "" && index > 0) {
      inputRefs.current[index - 1].focus();
    }

    setValue("otp", newOtp);
  };

  const onSubmit = (data) => {
    let otp = data.otp.join("");

    let submissionData = {
      otp: otp,
      session_id: userData.session_id,
      is_forget: userData.is_forget,
    }

    if (userData.email) {
      submissionData.email = userData.email
    }

    else {
      submissionData.phone_number = userData.phone_number
      submissionData.country_code = userData.country_code
    }

    const otpPromise = new Promise((otpResolve, otpReject) => {
      dispatch(
        otpVerify({
          payload: {
            ...submissionData,
          },
        })
      )
        .then((otpResponse) => {
          if (otpResponse.payload.code === 200 || otpResponse.payload.code === 204) {
            if (userData.is_forget) {
              otpResolve(otpResponse.payload);
              navigate("/setup-new-password", {
                state: {
                  id: otpResponse.payload.data.id
                }
              }
              )
            }
            else {
              const signupData = {
                ...userInfo
              }
              const signupPromise = new Promise((signupResolve, signupReject) => {
                dispatch(
                  signupUser({
                    payload: {
                      ...signupData
                    },
                  })
                )
                  .then((signupResponse) => {
                    if (signupResponse.payload.code === 200) {
                      signupResolve(signupResponse.payload);
                      otpResolve(signupResponse.payload);
                      navigate("/complete-profile");
                    } else {
                      signupReject(signupResponse.payload);
                    }
                  });
              });
              signupPromise.catch(error => {
                otpReject(error);
                navigate("/sign-up");
              });
            }
          }
          else {
            otpReject(otpResponse.payload)
          }
        });
    });

    showPromiseNotification(otpPromise);
  };

  return (
    <div className="signin-page-area">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-6 col-lg-7">
            <form className="signin-inner" onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                <div className="col-12">
                  <div className="single-input-inner style-bg-border">
                    <h4 className="SignInUpHeading text-center mb-2">
                      {t('otpHeading')}
                    </h4>
                    {userData?.email ? (
                      <p className="ForgotText">
                        {t('otpVerificationMsg', { field: t("email_address") })}
                        <span>
                          {" " + userData?.email}
                        </span>.
                      </p>
                    ) : (
                      <p className="ForgotText">
                        {t('otpVerificationMsg', { field: t("phone_number") })}
                        <span>
                          +{userData?.country_code} {userData?.phone_number}
                        </span>.
                      </p>
                    )}
                  </div>
                </div>
                <div className="col-12">
                  <div className="OtpInputMain">
                    <div className="OtpInputBox single-input-inner">
                      {Array(4).fill("").map((_, index) => (
                        <>
                          <input
                            key={index}
                            type="text"
                            {...register(`otp[${index}]`)}
                            className="InputNumberOtp"
                            placeholder="-"
                            maxLength={1} // Limit input to 1 digit
                            onChange={(e) => handleOtpChange(e, index)}
                            ref={(el) => (inputRefs.current[index] = el)} // Store ref for each input
                          />
                        </>
                      ))}
                    </div>
                    {errors.otp && (
                      <p className='text-danger text-center'>
                        {errors.otp.message || t("valid", { field: "OTP" })}
                      </p>
                    )}
                    <p className="ExpiredText">
                      {isExpired ? (
                        t("otpExpired")
                      ) : (
                        <>{t('expireIn')} <strong>{formatTime(remainingTimer)}</strong></>
                      )}
                    </p>
                  </div>
                </div>
                <div className="col-12 mb-4">
                  <button type='submit' className="btn btn-base w-100">
                    {t('verify')}
                  </button>
                </div>
                <div className="col-12 mb-4">
                  {canResend ? (
                    <p className="ExpiredText">{t('didntReceiveCode')} {' '}
                      <button type='button' className="btn border-0 m-0 p-0" onClick={handleResendOtp}>
                        {t('resend')}
                      </button>
                    </p>
                  ) : (
                    <span className="ResendText">
                      {/* {t('resendCode', {field : timer})} */}
                    </span>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
